import posthog from "posthog-js";
import { axiosBasic, getUserInfo } from "../../../api/axios";
import { config } from "../../../api/endPoints";
import { Common } from "../../../utils";

const { REST_API } = config;

// Action Types
export const NOTIFICATION_ACTION_TYPES = {
    GET_USER_NOTIFICATIONS_REQUEST: "GET_USER_NOTIFICATIONS_REQUEST",
    GET_USER_NOTIFICATIONS_SUCCESS: "GET_USER_NOTIFICATIONS_SUCCESS",
    GET_USER_NOTIFICATIONS_FAILURE: "GET_USER_NOTIFICATIONS_FAILURE",

    GET_UNSEEN_COUNTS_REQUEST: "GET_UNSEEN_COUNTS_REQUEST",
    GET_UNSEEN_COUNTS_SUCCESS: "GET_UNSEEN_COUNTS_SUCCESS",
    GET_UNSEEN_COUNTS_FAILURE: "GET_UNSEEN_COUNTS_FAILURE",

    MARK_ALL_NOTIFICATIONS_SEEN_REQUEST: "MARK_ALL_NOTIFICATIONS_SEEN_REQUEST",
    MARK_ALL_NOTIFICATIONS_SEEN_SUCCESS: "MARK_ALL_NOTIFICATIONS_SEEN_SUCCESS",
    MARK_ALL_NOTIFICATIONS_SEEN_FAILURE: "MARK_ALL_NOTIFICATIONS_SEEN_FAILURE",

    ADD_NOTIFICATION: "ADD_NOTIFICATION",
    UPDATE_NOTIFICATION: "UPDATE_NOTIFICATION",
    DELETE_NOTIFICATION: "DELETE_NOTIFICATION",
    INCREMENT_UNREAD_COUNT: "INCREMENT_UNREAD_COUNT",

    CLEAR_NOTIFICATIONS: "CLEAR_NOTIFICATIONS"
};

// Action Creators
export const getUserNotifications = (start = 0, page = 1, take = 10, isPreview = false) => {
    return async (dispatch, getState) => {
        const userId = getUserInfo()?.selfUserId ?? null;
        const userInput = { start, page, take, isPreview };
        try {
            // If this is a preview request and we already have more notifications than requested,
            // don't make the API call to avoid replacing the full list
            if (isPreview) {
                const currentNotifications = getState().notificationReducer.notifications;
                if (currentNotifications.length >= take) {
                    console.log('Preview request skipped - already have enough notifications');
                    return;
                }
            }

            dispatch({ type: NOTIFICATION_ACTION_TYPES.GET_USER_NOTIFICATIONS_REQUEST });

            const response = await axiosBasic.get(REST_API.Notification.GetNotifications, {
                params: {
                    start,
                    page,
                    take
                }
            });

            const success = response.data && response.data.success;
            if (typeof posthog !== "undefined" && posthog.capture) {
                posthog.capture("getUserNotifications", {
                    type: success ? "info" : "exception",
                    userId,
                    success,
                    userInput,
                    reason: success ? undefined : (response.data?.message || "Failed to fetch notifications"),
                });
            }
            if (success) {
                dispatch({
                    type: NOTIFICATION_ACTION_TYPES.GET_USER_NOTIFICATIONS_SUCCESS,
                    payload: {
                        notifications: response.data.data,
                        isRefresh: start === 0 && !isPreview, // Refresh only if start=0 and not a preview
                        isPreview: isPreview // Flag to indicate this is a preview load
                    }
                });
            } else {
                throw new Error(response.data?.message || 'Failed to fetch notifications');
            }
        } catch (error) {
            if (typeof posthog !== "undefined" && posthog.capture) {
                posthog.capture("getUserNotifications", {
                    type: "exception",
                    userId,
                    success: false,
                    userInput,
                    reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
                });
            }
            dispatch({
                type: NOTIFICATION_ACTION_TYPES.GET_USER_NOTIFICATIONS_FAILURE,
                payload: error.message
            });

            // Show error message only if it's not a network error
            if (error.response?.status !== 404) {
                Common.showErrorMessage('Failed to load notifications');
            }
        }
    };
};

export const getUnseenCounts = () => {
    return async (dispatch, getState) => {
        const userId = getUserInfo()?.selfUserId ?? null;
        const userInput = {};
        // Check if already loading
        const { unreadCountsLoading } = getState().notificationReducer;
        if (unreadCountsLoading) {
            return;
        }

        try {
            dispatch({ type: NOTIFICATION_ACTION_TYPES.GET_UNSEEN_COUNTS_REQUEST });

            const response = await axiosBasic.get(REST_API.Notification.GetUnSeenCounts);

            const success = response.data && response.data.success;
            if (typeof posthog !== "undefined" && posthog.capture) {
                posthog.capture("getUnseenCounts", {
                    type: success ? "info" : "exception",
                    userId,
                    success,
                    userInput,
                    reason: success ? undefined : (response.data?.message || "Failed to fetch unread counts"),
                });
            }
            if (success) {
                dispatch({
                    type: NOTIFICATION_ACTION_TYPES.GET_UNSEEN_COUNTS_SUCCESS,
                    payload: response.data.data
                });
            } else {
                throw new Error(response.data?.message || 'Failed to fetch unread counts');
            }
        } catch (error) {
            if (typeof posthog !== "undefined" && posthog.capture) {
                posthog.capture("getUnseenCounts", {
                    type: "exception",
                    userId,
                    success: false,
                    userInput,
                    reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
                });
            }
            dispatch({
                type: NOTIFICATION_ACTION_TYPES.GET_UNSEEN_COUNTS_FAILURE,
                payload: error.message
            });
        }
    };
};

export const markAllNotificationsAsSeen = () => {
    return async (dispatch) => {
        const userId = getUserInfo()?.selfUserId ?? null;
        const userInput = {};
        try {
            dispatch({ type: NOTIFICATION_ACTION_TYPES.MARK_ALL_NOTIFICATIONS_SEEN_REQUEST });

            const response = await axiosBasic.put(REST_API.Notification.UpdateToSeen, {});

            const success = response.data && response.data.success;
            if (typeof posthog !== "undefined" && posthog.capture) {
                posthog.capture("markAllNotificationsAsSeen", {
                    type: success ? "info" : "exception",
                    userId,
                    success,
                    userInput,
                    reason: success ? undefined : (response.data?.message || "Failed to mark all notifications as seen"),
                });
            }
            if (success) {
                dispatch({
                    type: NOTIFICATION_ACTION_TYPES.MARK_ALL_NOTIFICATIONS_SEEN_SUCCESS
                });
                // Refresh unread count after marking all as seen
                dispatch(getUnseenCounts());
            } else {
                throw new Error(response.data?.message || 'Failed to mark all notifications as seen');
            }
        } catch (error) {
            if (typeof posthog !== "undefined" && posthog.capture) {
                posthog.capture("markAllNotificationsAsSeen", {
                    type: "exception",
                    userId,
                    success: false,
                    userInput,
                    reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
                });
            }
            dispatch({
                type: NOTIFICATION_ACTION_TYPES.MARK_ALL_NOTIFICATIONS_SEEN_FAILURE,
                payload: error.message
            });
        }
    };
};

export const addNotification = (notification) => {
    return {
        type: NOTIFICATION_ACTION_TYPES.ADD_NOTIFICATION,
        payload: notification
    };
};

export const updateNotification = (notification) => {
    return {
        type: NOTIFICATION_ACTION_TYPES.UPDATE_NOTIFICATION,
        payload: notification
    };
};

export const deleteNotification = (notificationId) => {
    return {
        type: NOTIFICATION_ACTION_TYPES.DELETE_NOTIFICATION,
        payload: notificationId
    };
};

export const clearNotifications = () => {
    return {
        type: NOTIFICATION_ACTION_TYPES.CLEAR_NOTIFICATIONS
    };
};

export const incrementUnreadCount = () => {
    return {
        type: NOTIFICATION_ACTION_TYPES.INCREMENT_UNREAD_COUNT
    };
};

// Real-time notification handler
export const handleRealtimeNotification = (notification) => {
    return (dispatch) => {
        dispatch(addNotification(notification));
        // Refresh unread count when new notification is added
        dispatch(getUnseenCounts());
    };
};
